import {useEffect, useState} from "react";
import {Button, Card, Col, message, Row, Statistic, Table} from "antd";
import {PrinterOutlined, ReloadOutlined} from "@ant-design/icons";
import Api from "../../services/Api";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import flexCurrency from "../../utils/FlexCurrency";

dayjs.locale("tr");

const printStyle = `
  .rst-yillik-table .ant-table-cell { padding: 3px !important; }
  @media print {
    body * { visibility: hidden; }
    #rst-yillik-print-area, #rst-yillik-print-area * { visibility: visible; }
    #rst-yillik-print-area { position: absolute; top: 0; left: 0; width: 100%; }
    .no-print { display: none !important; }
  }
`;

const RENKLER = {
    teras: "#1677ff",
    bufe:  "#fa8c16",
    salon: "#722ed1",
    genel: "#52c41a",
};

const columns = [
    {
        title: "Tarih",
        dataIndex: "Gun",
        render: g => dayjs(g).format("DD MMMM YYYY"),
    },
    {
        title: <span style={{color: RENKLER.teras}}>Teras (₺)</span>,
        dataIndex: "TerasToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.teras}}>{flexCurrency(v)}</span>,
    },
    {
        title: <span style={{color: RENKLER.bufe}}>Büfe (₺)</span>,
        dataIndex: "BufeToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.bufe}}>{flexCurrency(v)}</span>,
    },
    {
        title: <span style={{color: RENKLER.salon}}>Salon (₺)</span>,
        dataIndex: "SalonToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.salon}}>{flexCurrency(v)}</span>,
    },
    {
        title: "Genel Toplam (₺)",
        dataIndex: "GenelToplam",
        className: "text-align-right",
        render: v => <b style={{color: RENKLER.genel}}>{flexCurrency(v)}</b>,
    },
];

const groupData = (data) => {
    const groups = {};
    data.forEach(item => {
        const yil = dayjs(item.Gun).format("YYYY");
        const ay  = dayjs(item.Gun).format("YYYY-MM");
        if (!groups[yil])     groups[yil] = {};
        if (!groups[yil][ay]) groups[yil][ay] = [];
        groups[yil][ay].push(item);
    });

    return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map(yil => {
            const aylar = Object.keys(groups[yil])
                .sort((a, b) => b.localeCompare(a))
                .map(ay => {
                    const items = [...groups[yil][ay]].sort(
                        (a, b) => new Date(b.Gun) - new Date(a.Gun)
                    );
                    const total = {
                        TerasToplam: items.reduce((s, i) => s + i.TerasToplam, 0),
                        BufeToplam:  items.reduce((s, i) => s + i.BufeToplam,  0),
                        SalonToplam: items.reduce((s, i) => s + i.SalonToplam, 0),
                        GenelToplam: items.reduce((s, i) => s + i.GenelToplam, 0),
                    };
                    const label = dayjs(`${ay}-01`).format("MMMM YYYY");
                    return {ay, label, items, total};
                });

            const yilToplam = aylar.reduce((acc, {total}) => ({
                TerasToplam: acc.TerasToplam + total.TerasToplam,
                BufeToplam:  acc.BufeToplam  + total.BufeToplam,
                SalonToplam: acc.SalonToplam  + total.SalonToplam,
                GenelToplam: acc.GenelToplam  + total.GenelToplam,
            }), {TerasToplam: 0, BufeToplam: 0, SalonToplam: 0, GenelToplam: 0});

            return {yil, aylar, yilToplam};
        });
};

/* ── Ay özet şeridi ─────────────────────────────────────── */
const AyOzet = ({total}) => (
    <Row
        gutter={[16, 8]}
        style={{padding: "10px 16px", background: "#fafafa", borderBottom: "1px solid #f0f0f0"}}
    >
        <Col xs={12} sm={6}>
            <Statistic
                title="Teras"
                value={total.TerasToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.teras}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Büfe"
                value={total.BufeToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.bufe}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Salon"
                value={total.SalonToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.salon}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Toplam Tutar"
                value={total.GenelToplam}
                suffix="₺"
                valueStyle={{fontSize: 14, color: RENKLER.genel, fontWeight: 700}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
    </Row>
);

/* ── Yıl özet kartı ─────────────────────────────────────── */
const YilOzet = ({yil, yilToplam}) => (
    <Card
        style={{
            marginBottom: 12,
            background: "linear-gradient(135deg, #f6ffed 0%, #e6f4ff 100%)",
            border: "1px solid #91caff",
        }}
        bodyStyle={{padding: "12px 16px"}}
    >
        <Row align="middle" gutter={[24, 8]}>
            <Col xs={24} sm={4}>
                <div style={{fontSize: 20, fontWeight: 800, color: "#1677ff", letterSpacing: 1}}>
                    {yil}
                </div>
                <div style={{fontSize: 11, color: "#888", marginTop: 2}}>Yıllık Özet</div>
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Teras"
                    value={yilToplam.TerasToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.teras, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Büfe"
                    value={yilToplam.BufeToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.bufe, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Salon"
                    value={yilToplam.SalonToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.salon, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Genel Toplam"
                    value={yilToplam.GenelToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 16, color: RENKLER.genel, fontWeight: 800}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
        </Row>
    </Card>
);

/* ── Ana bileşen ────────────────────────────────────────── */
const RestaurantYillikOzetRapor = () => {
    const [yearData, setYearData] = useState([]);
    const [loading, setLoading]   = useState(false);

    const getData = () => {
        setLoading(true);
        Api.get("api/ToyaPos/GunlukOzet")
            .then(res => setYearData(groupData(res.data)))
            .catch(() => message.error("Veriler alınırken hata oluştu."))
            .finally(() => setLoading(false));
    };

    useEffect(() => { getData(); }, []);

    return (
        <div className={"p-1"}>
            <style>{printStyle}</style>

            {/* Üst bar */}
            <Card
                className={"no-print"}
                style={{marginBottom: 12}}
                bodyStyle={{padding: "10px 16px"}}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <span style={{fontWeight: 700, fontSize: 15}}>Restaurant Yıllık Özet Raporu</span>
                    </Col>
                    <Col>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={getData}
                            loading={loading}
                            style={{marginRight: 8}}
                        >
                            Yenile
                        </Button>
                        {yearData.length > 0 && (
                            <Button
                                icon={<PrinterOutlined/>}
                                onClick={() => window.print()}
                            >
                                Yazdır
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card>

            {loading && <Card loading style={{marginBottom: 12}}/>}

            <div id={"rst-yillik-print-area"}>
                {yearData.map(({yil, aylar, yilToplam}) => (
                    <div key={yil} style={{marginBottom: 24}}>
                        <YilOzet yil={yil} yilToplam={yilToplam}/>

                        {aylar.map(({ay, label, items, total}) => (
                            <Card
                                key={ay}
                                size={"small"}
                                title={<span style={{fontWeight: 600}}>{label}</span>}
                                bodyStyle={{padding: 0}}
                                style={{marginBottom: 8}}
                            >
                                <AyOzet total={total}/>
                                <Table
                                    className={"rst-yillik-table"}
                                    size={"small"}
                                    bordered
                                    dataSource={items}
                                    rowKey={r => r.Gun}
                                    columns={columns}
                                    pagination={false}
                                />
                            </Card>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RestaurantYillikOzetRapor;
